// DataTable — wraps the bare <Table> primitives with sort / filter /
// export / sticky header / pagination / column visibility / saved-views.
// Per chakra_dashboard#11.
//
// Composes <Table>, <Thead>, <Th>, <Tbody>, <Tr>, <Td> from ui.tsx so the
// editorial-monospace aesthetic stays identical. The chrome (header
// controls, pagination strip, export menu) is in this file.
//
// Per-column behavior is driven by ColumnConfig<T>. Numeric columns must
// NOT have filterable:true — operator instruction (range filters are
// noisy and out of scope; revisit if requested).

"use client";

import { useEffect, useMemo, useState } from "react";
import { Table, Tbody, Tr, Td, Button, MenuItem, ToggleChip } from "./ui";

export type ColumnConfig<T> = {
  /** Unique key within the table; used for saved-view JSON shape. */
  key: string;
  header: string;
  /** Optional formula/definition. Renders an ⓘ glyph next to the header
   *  with a floating tooltip on hover. Not included in exports. */
  headerHelp?: string;
  align?: "left" | "right";
  /** Renderer; required so existing pill/format/className stays bespoke. */
  cell: (row: T) => React.ReactNode;
  /** Default true. Opt out for action-only columns (e.g. → arrow). */
  sortable?: boolean;
  /** Underlying value for sort. NOT the formatted string — for currency
   *  columns this MUST be the raw number, otherwise "₹1,00,000" sorts
   *  next to "₹999". For Direction (LONG/SHORT/...) use a priority
   *  integer (LONG:0, SHORT:1, NEUTRAL:2, CLOSED:3, "N/A":4). */
  sortAccessor?: (row: T) => string | number;
  /** Default false. MUST be false for numeric columns. */
  filterable?: boolean;
  /** Text to match for free-text filter (case-insensitive substring). */
  filterValues?: (row: T) => string;
  /** When set, render as multi-select chip filter instead of text box.
   *  List the full enum so empty groups still appear. */
  filterEnum?: string[];
  /** Value used in CSV/XLSX/PDF export. Falls back to sortAccessor, then
   *  to the cell-rendered text. */
  exportValue?: (row: T) => string | number;
  hideable?: boolean;
  defaultHidden?: boolean;
};

type SortState = { key: string; dir: "asc" | "desc" } | null;
type FilterState = Record<string, string | string[]>;     // text → string; enum → string[]
type Preset = { sort: SortState; filters: FilterState; hidden: string[] };

type Props<T> = {
  tableKey: string;
  data: T[];
  columns: ColumnConfig<T>[];
  rowKey: (row: T) => string;
  /** Rendered as a single <Tr> AFTER the data rows. Excluded from
   *  sort/filter/paginate/export. Pass the same JSX you'd write inline. */
  totalsRow?: React.ReactNode;
  /** Row click → navigate. Mirrors <Tr asLinkHref>. */
  asLinkHref?: (row: T) => string;
  defaultSort?: SortState;
  /** Pagination: default true if data.length > 50. Pass false to disable,
   *  a number for explicit page size, or `true` for auto. */
  paginate?: boolean | { pageSize: number };
  /** Freeze the leftmost visible column against the viewport during
   *  horizontal scroll. Used on wide tables (trades, cycle drilldown,
   *  per-broker rollup) so the identifying column stays in view. See
   *  <Table sticky> in ui.tsx for the mechanism. */
  stickyFirstColumn?: boolean;
};

const PRESET_KEY = (k: string) => `chakra:datatable:${k}:v1`;

function loadPreset(tableKey: string): Partial<Preset> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(PRESET_KEY(tableKey));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePreset(tableKey: string, preset: Preset) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PRESET_KEY(tableKey), JSON.stringify(preset));
  } catch {
    // localStorage may be full / disabled — silent no-op
  }
}

export function DataTable<T>({
  tableKey,
  data,
  columns,
  rowKey,
  totalsRow,
  asLinkHref,
  defaultSort = null,
  paginate,
  stickyFirstColumn = false,
}: Props<T>) {
  // Hydrate from localStorage on mount only. Subsequent state changes
  // write back via the effect below.
  const [sort, setSort] = useState<SortState>(defaultSort);
  const [filters, setFilters] = useState<FilterState>({});
  const [hidden, setHidden] = useState<string[]>(
    columns.filter((c) => c.defaultHidden).map((c) => c.key),
  );
  const [hydrated, setHydrated] = useState(false);
  const [page, setPage] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);

  useEffect(() => {
    const p = loadPreset(tableKey);
    if (p.sort !== undefined) setSort(p.sort);
    if (p.filters) setFilters(p.filters);
    if (p.hidden) setHidden(p.hidden);
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  useEffect(() => {
    if (!hydrated) return;
    savePreset(tableKey, { sort, filters, hidden });
  }, [tableKey, hydrated, sort, filters, hidden]);

  const visibleCols = useMemo(
    () => columns.filter((c) => !hidden.includes(c.key)),
    [columns, hidden],
  );

  // Filter pipeline
  const filtered = useMemo(() => {
    return data.filter((row) => {
      for (const c of columns) {
        const v = filters[c.key];
        if (!v) continue;
        if (Array.isArray(v)) {
          if (v.length === 0) continue;
          const val = c.filterValues ? c.filterValues(row) : String(c.cell(row));
          if (!v.includes(val)) return false;
        } else {
          const needle = v.trim().toLowerCase();
          if (!needle) continue;
          const hay = (c.filterValues ? c.filterValues(row) : String(c.cell(row))).toLowerCase();
          if (!hay.includes(needle)) return false;
        }
      }
      return true;
    });
  }, [data, columns, filters]);

  // Sort pipeline — sort uses the underlying value, never the formatted string.
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !col.sortAccessor) return filtered;
    const acc = col.sortAccessor;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filtered, columns, sort]);

  // Pagination
  const shouldPaginate =
    paginate === false ? false : paginate === true ? true : typeof paginate === "object" ? true : sorted.length > 50;
  const pageSize = typeof paginate === "object" ? paginate.pageSize : 50;
  const pageCount = shouldPaginate ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount - 1);
  const pagedRows = shouldPaginate
    ? sorted.slice(safePage * pageSize, (safePage + 1) * pageSize)
    : sorted;

  const toggleSort = (key: string) => {
    setPage(0);
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const setFilter = (key: string, v: string | string[]) => {
    setPage(0);
    setFilters((cur) => ({ ...cur, [key]: v }));
  };

  const toggleHidden = (key: string) => {
    setHidden((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const resetView = () => {
    setSort(defaultSort);
    setFilters({});
    setHidden(columns.filter((c) => c.defaultHidden).map((c) => c.key));
    setPage(0);
  };

  const exportRows = () => {
    return sorted.map((row) => {
      const obj: Record<string, string | number> = {};
      for (const c of visibleCols) {
        let val: string | number;
        if (c.exportValue) val = c.exportValue(row);
        else if (c.sortAccessor) val = c.sortAccessor(row);
        else {
          const rendered = c.cell(row);
          val = typeof rendered === "string" || typeof rendered === "number" ? rendered : "";
        }
        obj[c.header] = val;
      }
      return obj;
    });
  };

  // Lazy-load export libs only on click — they're ~150 KB combined.
  const downloadCSV = async () => {
    const Papa = (await import("papaparse")).default;
    const csv = Papa.unparse(exportRows());
    triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${tableKey}.csv`);
  };
  const downloadXLSX = async () => {
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.json_to_sheet(exportRows());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tableKey.slice(0, 31));
    XLSX.writeFile(wb, `${tableKey}.xlsx`);
  };
  const downloadPDF = async () => {
    const jsPDF = (await import("jspdf")).default;
    const autoTable = (await import("jspdf-autotable")).default;
    const rows = exportRows();
    const headers = visibleCols.map((c) => c.header);
    const body = rows.map((r) => headers.map((h) => String(r[h] ?? "")));
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(11);
    doc.text(tableKey, 14, 12);
    autoTable(doc, { head: [headers], body, startY: 16, styles: { fontSize: 8 } });
    doc.save(`${tableKey}.pdf`);
  };

  const filterableCols = columns.filter((c) => c.filterable && !hidden.includes(c.key));

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {filterableCols.map((c) => (
          <ColumnFilter
            key={c.key}
            col={c}
            value={filters[c.key]}
            onChange={(v) => setFilter(c.key, v)}
            data={data}
          />
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            onClick={resetView}
            title="Reset to default view (clears saved sort, filter, column visibility)"
          >
            Reset
          </Button>
          <MenuButton
            label="Columns"
            open={showVisibilityMenu}
            onToggle={() => setShowVisibilityMenu((v) => !v)}
          >
            {columns
              .filter((c) => c.hideable !== false)
              .map((c) => (
                <label key={c.key} className="flex items-center gap-2 px-3 py-1 text-sm cursor-pointer hover:bg-white/[0.04]">
                  <input
                    type="checkbox"
                    checked={!hidden.includes(c.key)}
                    onChange={() => toggleHidden(c.key)}
                  />
                  <span>{c.header}</span>
                </label>
              ))}
          </MenuButton>
          <MenuButton
            label="Export"
            open={showExportMenu}
            onToggle={() => setShowExportMenu((v) => !v)}
          >
            <MenuItem onClick={() => { setShowExportMenu(false); downloadCSV(); }}>CSV</MenuItem>
            <MenuItem onClick={() => { setShowExportMenu(false); downloadXLSX(); }}>XLSX</MenuItem>
            <MenuItem onClick={() => { setShowExportMenu(false); downloadPDF(); }}>PDF</MenuItem>
          </MenuButton>
        </div>
      </div>

      {/* Table
       *   stickyFirstColumn: emit ``sticky left-0 bg-background`` on the
       *   first <th>/<Td> so it freezes against the viewport during
       *   horizontal scroll. Header row also stays sticky at the top;
       *   the corner cell needs the higher z-index so it wins both
       *   axes when scrolled together. See ui.tsx <Table sticky> for
       *   the border-separate mechanism. */}
      <Table sticky={stickyFirstColumn}>
        <thead className="border-b border-rule sticky top-0 bg-background z-10">
          <tr>
            {visibleCols.map((c, i) => {
              const stickyCls =
                stickyFirstColumn && i === 0
                  ? `sticky left-0 bg-background ${i === 0 ? "z-20" : ""}`
                  : "";
              return (
                <th
                  key={c.key}
                  className={`eyebrow py-1.5 pr-4 ${c.align === "right" ? "text-right" : "text-left"} font-normal text-mute ${c.sortable !== false ? "cursor-pointer select-none hover:text-foreground" : ""} ${stickyCls}`}
                  onClick={c.sortable !== false ? () => toggleSort(c.key) : undefined}
                >
                <span>{c.header}</span>
                {c.headerHelp && (
                  <span
                    className="group/help relative ml-1 inline-block cursor-help align-middle"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={c.headerHelp}
                  >
                    <span className="text-mute hover:text-foreground">ⓘ</span>
                    <span
                      className={`pointer-events-none absolute top-full z-30 mt-1.5 hidden w-72 whitespace-pre-line border border-rule bg-background p-3 text-left text-xs font-normal normal-case tracking-normal text-foreground shadow-lg group-hover/help:block ${c.align === "right" ? "right-0" : "left-0"}`}
                    >
                      {c.headerHelp}
                    </span>
                  </span>
                )}
                {sort && sort.key === c.key && (
                  <span className="ml-1 text-foreground">{sort.dir === "asc" ? "▲" : "▼"}</span>
                )}
                </th>
              );
            })}
          </tr>
        </thead>
        <Tbody>
          {pagedRows.map((row) => (
            <Tr key={rowKey(row)} asLinkHref={asLinkHref ? asLinkHref(row) : undefined}>
              {visibleCols.map((c, i) => (
                <Td
                  key={c.key}
                  align={c.align ?? "left"}
                  sticky={stickyFirstColumn && i === 0}
                  // whitespace-nowrap: keep values (₹8.5L, dates, pills) on one
                  // line — the table already scrolls horizontally (overflow-x on
                  // <Table>), so cells should never wrap-mangle on narrow/mobile
                  // widths. In stickyFirstColumn mode the table is border-separate,
                  // where the <Tr> bottom-border (row hairline) is dropped per the
                  // CSS separated-borders model — draw it on the cells instead so
                  // rows stay separated (matches the portfolio-summary pivots).
                  className={`whitespace-nowrap ${stickyFirstColumn ? "border-b border-rule/50" : ""}`}
                >
                  {c.cell(row)}
                </Td>
              ))}
            </Tr>
          ))}
          {totalsRow}
        </Tbody>
      </Table>

      {/* Pagination strip */}
      {shouldPaginate && pageCount > 1 && (
        <div className="flex items-center gap-3 text-xs text-mute">
          <span>
            Showing {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, sorted.length)} of {sorted.length}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}>
              Prev
            </Button>
            <span className="num">Page {safePage + 1} / {pageCount}</span>
            <Button size="sm" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ColumnFilter<T>({
  col,
  value,
  onChange,
  data,
}: {
  col: ColumnConfig<T>;
  value: string | string[] | undefined;
  onChange: (v: string | string[]) => void;
  data: T[];
}) {
  // Multi-select chip when filterEnum provided OR when distinct values are
  // few and the column is short — chip mode is more discoverable than
  // free-text search for fixed enums.
  if (col.filterEnum) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="eyebrow text-mute">{col.header}:</span>
        {col.filterEnum.map((opt) => {
          const isOn = selected.includes(opt);
          return (
            <ToggleChip
              key={opt}
              selected={isOn}
              onClick={() => onChange(isOn ? selected.filter((o) => o !== opt) : [...selected, opt])}
            >
              {opt}
            </ToggleChip>
          );
        })}
        {selected.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onChange([])}>clear</Button>
        )}
      </div>
    );
  }
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="eyebrow text-mute">{col.header}:</span>
      <input
        type="text"
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="filter…"
        className="bg-transparent border border-rule px-2 py-1 text-sm focus:border-foreground focus:outline-none"
      />
    </label>
  );
}

function MenuButton({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <Button size="sm" onClick={onToggle}>
        {label} ▾
      </Button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 min-w-[10rem] border border-rule bg-background"
          onMouseLeave={onToggle}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
